"use client";

import type {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import {publicationFailureKeys} from "@/utils/workflows/workflowPublication";
import type {ChatWorkflowPublication} from "./useChatWorkflowPublication";

/** What a chat publication did, including registrations that still need attention. */
export default function CompiledWorkflowPublicationOutcome({publication}: {publication: ChatWorkflowPublication}) {
   const {t} = useTranslation("ask");
   const {t: wt} = useTranslation("workflows");
   if (publication.status === "already_published") {
      return <output className="block text-sm text-green-700 dark:text-green-400">{t("workflowPanel.publication.alreadyPublished", {revision: publication.workflow.revision})}</output>;
   }
   if (publication.status === "published") {
      const failures = publicationFailureKeys(publication);
      if (failures.length === 0) {
         return <output className="block text-sm text-green-700 dark:text-green-400">{t("workflowPanel.publication.published", {revision: publication.workflow.revision})}</output>;
      }
      return (
         <div role="alert" className="space-y-1 text-sm text-amber-800 dark:text-amber-200">
            <p>{t("workflowPanel.publication.partial", {revision: publication.workflow.revision})}</p>
            <ul className="list-disc pl-5">{failures.map((key) => <li key={key}>{wt(key)}</li>)}</ul>
         </div>
      );
   }
   const message = publicationErrorMessage(publication, t);
   return <p role="alert" className="text-sm text-destructive">{message}</p>;
}

type FailedPublication = Exclude<ChatWorkflowPublication, {status: "published" | "already_published"}>;

function publicationErrorMessage(publication: FailedPublication, t: TFunction<"ask">): string {
   switch (publication.status) {
      case "stale":
         return t(publication.current ? "workflowPanel.publication.stale" : "workflowPanel.publication.gone");
      case "lookup_failed":
         return t("workflowPanel.publication.lookupFailed");
      case "canvas_not_saved":
         return t("workflowPanel.publication.canvasNotSaved");
      case "check_failed":
         return publication.error
            ? t("workflowPanel.publication.checkFailed", {error: publication.error})
            : t("workflowPanel.publication.checkFailedGeneric");
      default:
         return publication.error
            ? t("workflowPanel.publication.publishFailed", {error: publication.error})
            : t("workflowPanel.publication.publishFailedGeneric");
   }
}
